import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { PartnerDestination } from "@/types/partner-settings";
import { useSearchDestinations } from "../hooks/partner-settings.hooks";
import {
  destinationLabel,
  destinationSelectState,
  normalizeDestinationQuery,
  SEARCH_DEBOUNCE_MS,
} from "../utils/partnerDestinationSelect";

interface PartnerDestinationSelectProps {
  id: string;
  /** Name of the hotel's current city. */
  currentName?: string | null;
  disabled?: boolean;
  /** Saving, or waiting for the activity sync after a change. */
  busy?: boolean;
  busyText?: string | null;
  onSelect: (destination: PartnerDestination) => void;
}

/**
 * City selector: type-ahead over the backend city cache (full and partial
 * names). "City search" asks the provider and shows only when the cache has no
 * match for the typed text.
 */
export const PartnerDestinationSelect: React.FC<
  PartnerDestinationSelectProps
> = ({ id, currentName, disabled, busy, busyText, onSelect }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [remoteQuery, setRemoteQuery] = React.useState<string | null>(null);

  React.useEffect(() => {
    const timer = setTimeout(
      () => setQuery(normalizeDestinationQuery(input)),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [input]);

  React.useEffect(() => {
    setRemoteQuery(null);
  }, [query]);

  const cache = useSearchDestinations(query);
  const remote = useSearchDestinations(query, {
    remote: true,
    enabled: remoteQuery !== null && remoteQuery === query,
  });
  const remoteTried = remoteQuery !== null && remoteQuery === query;

  const state = destinationSelectState({
    query,
    results: remoteTried ? remote.data : cache.data,
    isLoading: cache.isLoading,
    isError: cache.isError,
    remoteTried,
    remoteLoading: remoteTried && remote.isFetching,
    remoteError: remoteTried && remote.isError,
  });
  const results = (remoteTried ? remote.data : cache.data) ?? [];

  const startCitySearch = () => {
    if (remoteTried) {
      remote.refetch().catch(() => undefined);
    } else {
      setRemoteQuery(query);
    }
  };

  const choose = (destination: PartnerDestination) => {
    setOpen(false);
    setInput("");
    setQuery("");
    onSelect(destination);
  };

  const isDisabled = !!disabled || !!busy;

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-md">
        {t("partners.hotelSettings.city.label")}
      </Label>
      <Popover open={open && !isDisabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={isDisabled}
            className="w-full max-w-sm justify-between"
          >
            <span className="truncate">
              {currentName || t("partners.hotelSettings.city.none")}
            </span>
            {busy && <Loader2 className="size-4 animate-spin" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-80 flex-col gap-2"
          // Inside a modal Dialog this content is portaled outside the dialog,
          // and the dialog's scroll lock swallows the wheel and touch scroll
          // that reach the document. Stopping the bubbling keeps the list
          // scrollable; on a plain page nothing listens, so nothing changes.
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Input
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("partners.hotelSettings.city.placeholder")}
          />
          <div
            className="flex max-h-64 flex-col overflow-y-auto"
            role="listbox"
          >
            {state === "loading" && (
              <p className="p-2 text-sm text-black/55">
                {t(
                  remoteTried
                    ? "partners.hotelSettings.city.citySearching"
                    : "partners.hotelSettings.city.loading",
                )}
              </p>
            )}
            {state === "error" && (
              <p className="text-destructive p-2 text-sm">
                {t("partners.hotelSettings.city.error")}
              </p>
            )}
            {state === "results" &&
              results.map((destination) => (
                <button
                  key={destination.destinationId}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="hover:bg-accent cursor-pointer truncate rounded-md px-2 py-2 text-left text-sm"
                  onClick={() => choose(destination)}
                >
                  {destinationLabel(destination)}
                </button>
              ))}
            {state === "not-found" && (
              <p className="p-2 text-sm text-black/55">
                {t("partners.hotelSettings.city.notFound")}
              </p>
            )}
            {state === "empty-can-search" && (
              <div className="flex flex-col gap-2 p-2">
                <p className="text-sm text-black/55">
                  {t("partners.hotelSettings.city.empty")}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={startCitySearch}
                >
                  {t("partners.hotelSettings.city.citySearch")}
                </Button>
              </div>
            )}
            {state === "remote-error" && (
              <div className="flex flex-col gap-2 p-2">
                <p className="text-destructive text-sm">
                  {t("partners.hotelSettings.city.citySearchError")}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={startCitySearch}
                >
                  {t("partners.hotelSettings.city.citySearch")}
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <p className="text-xs leading-5 text-black/55">
        {busyText ?? t("partners.hotelSettings.city.hint")}
      </p>
    </div>
  );
};
